import { useState, useEffect } from 'react';
import {
  ClipboardList,
  Settings,
  BarChart3,
  Users,
  Plus,
  Edit2,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  FileSpreadsheet,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import {
  getSurveySections,
  createSurveySection,
  updateSurveySection,
  deleteSurveySection,
  resetSurveyToDefaults,
  createSurveyQuestion,
  updateSurveyQuestion,
  deleteSurveyQuestion,
  getSurveySettings,
  updateSurveySettings,
  getSurveyFilters,
  getSurveyResultsSummary,
  getSurveyResultsByProfessor,
  getSurveyResultsByMatiere,
  getSurveyComments,
  getSurveyParticipation,
  exportSurveyExcel,
} from '@/services/courseEvaluationService';
import type {
  SurveySection,
  SurveySettings,
  SurveyFilters,
  SurveyResultFilters,
  SurveyResultsSummary,
  ProfessorResult,
  MatiereResult,
  CommentResponse,
  ParticipationRow,
} from '@/types/course-evaluation.types';

type TabKey = 'config' | 'settings' | 'results' | 'participation';

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: 'config', label: 'Configuration', icon: <ClipboardList size={18} /> },
  { key: 'settings', label: 'Paramètres', icon: <Settings size={18} /> },
  { key: 'results', label: 'Résultats', icon: <BarChart3 size={18} /> },
  { key: 'participation', label: 'Participation', icon: <Users size={18} /> },
];

export default function CourseEvaluationPage() {
  // Permission check - must be defined before useState for initial tab
  const { hasPermission } = usePermissions();
  const canView = hasPermission(Permission.EVALUATION_VIEW);
  const canManage = hasPermission(Permission.EVALUATION_MANAGE);
  const canExport = hasPermission(Permission.EVALUATION_EXPORT);

  // Filter tabs based on permissions - only show config/settings if user can manage
  const visibleTabs = canManage ? TABS : TABS.filter((tab) => tab.key === 'results' || tab.key === 'participation');

  const [activeTab, setActiveTab] = useState<TabKey>(canManage ? 'config' : 'results');
  const [loading, setLoading] = useState(true);

  // Config tab state
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Settings tab state
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [settingsForm, setSettingsForm] = useState<Partial<SurveySettings>>({});

  // Results tab state
  const [filters, setFilters] = useState<SurveyFilters | null>(null);
  const [resultFilters, setResultFilters] = useState<SurveyResultFilters>({});
  const [summary, setSummary] = useState<SurveyResultsSummary | null>(null);
  const [professorResults, setProfessorResults] = useState<ProfessorResult[]>([]);
  const [matiereResults, setMatiereResults] = useState<MatiereResult[]>([]);
  const [comments, setComments] = useState<CommentResponse[]>([]);
  const [resultsView, setResultsView] = useState<'summary' | 'professor' | 'matiere' | 'comments'>('summary');

  // Participation tab state
  const [participation, setParticipation] = useState<ParticipationRow[]>([]);

  // Modal states
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<SurveySection | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<{ sectionId: string; question?: any } | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'danger' | 'warning';
    onConfirm: () => void;
    loading?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'danger',
    onConfirm: () => {},
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'results') {
      loadResults();
    } else if (activeTab === 'participation') {
      loadParticipation();
    }
  }, [activeTab, resultFilters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [sectionsData, settingsData, filtersData] = await Promise.all([
        getSurveySections(),
        getSurveySettings(),
        getSurveyFilters(),
      ]);
      setSections(sectionsData);
      setSettings(settingsData);
      setSettingsForm(settingsData);
      setFilters(filtersData);

      // Set default filter to current academic year
      const currentYear = filtersData.academicYears.find((y) => y.isCurrent);
      if (currentYear) {
        setResultFilters((prev) => ({ ...prev, academicYearId: currentYear.id }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResults = async () => {
    try {
      const [summaryData, profData, matiereData, commentsData] = await Promise.all([
        getSurveyResultsSummary(resultFilters),
        getSurveyResultsByProfessor(resultFilters),
        getSurveyResultsByMatiere(resultFilters),
        getSurveyComments(resultFilters),
      ]);
      setSummary(summaryData);
      setProfessorResults(profData);
      setMatiereResults(matiereData);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  const loadParticipation = async () => {
    try {
      const data = await getSurveyParticipation(resultFilters);
      setParticipation(data);
    } catch (error) {
      console.error('Error loading participation:', error);
    }
  };

  const handleResetDefaults = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Réinitialiser le sondage',
      message: 'Êtes-vous sûr de vouloir réinitialiser le sondage aux valeurs par défaut? Cette action est irréversible.',
      variant: 'warning',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await resetSurveyToDefaults();
          const sectionsData = await getSurveySections();
          setSections(sectionsData);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, loading: false }));
        } catch (error) {
          console.error('Error resetting:', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handleSaveSettings = async () => {
    try {
      // Weight-threshold is the only supported activation mode
      const updated = await updateSurveySettings({
        isActive: settingsForm.isActive,
        thresholdPercentage: settingsForm.thresholdPercentage,
        activationMode: 'AUTOMATIC_THRESHOLD',
      });
      setSettings(updated);
      setSettingsForm(updated);
      alert('Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await exportSurveyExcel(resultFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluation-results-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer la section',
      message: 'Êtes-vous sûr de vouloir supprimer cette section? Toutes les questions associées seront également supprimées.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteSurveySection(sectionId);
          setSections((prev) => prev.filter((s) => s.id !== sectionId));
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, loading: false }));
        } catch (error) {
          console.error('Error deleting section:', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handleDeleteQuestion = (questionId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Supprimer la question',
      message: 'Êtes-vous sûr de vouloir supprimer cette question?',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteSurveyQuestion(questionId);
          const sectionsData = await getSurveySections();
          setSections(sectionsData);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false, loading: false }));
        } catch (error) {
          console.error('Error deleting question:', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Filière/Option cascade: the Filière select lists parent filières only; when
  // the selected filière has options, an extra Option select appears. The
  // filiereId filter carries the option id when one is chosen, else the parent
  // id (backend expands a parent to include its options).
  const allFilieres = filters?.filieres ?? [];
  const selectedFiliere = allFilieres.find((f) => f.id === resultFilters.filiereId);
  const parentFiliere = selectedFiliere?.parentCode
    ? allFilieres.find((f) => f.code === selectedFiliere.parentCode)
    : selectedFiliere;
  const parentFilieres = allFilieres.filter((f) => !f.parentCode);
  const filiereOptions = parentFiliere ? allFilieres.filter((f) => f.parentCode === parentFiliere.code) : [];

  // Shared filter selects for the Résultats and Participation tabs.
  // Campus options are already scoped server-side to the user's campuses.
  const renderFilterSelects = () => (
    <div className="flex flex-wrap gap-4 items-end">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Année académique</label>
        <select
          value={resultFilters.academicYearId ?? ''}
          onChange={(e) => setResultFilters((prev) => ({ ...prev, academicYearId: e.target.value || undefined }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Toutes</option>
          {filters?.academicYears.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name} {y.isCurrent && '(Actuelle)'}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Campus</label>
        <select
          value={resultFilters.campusId ?? ''}
          onChange={(e) => setResultFilters((prev) => ({ ...prev, campusId: e.target.value || undefined }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous</option>
          {filters?.campuses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
        <select
          value={resultFilters.semesterNumber ?? ''}
          onChange={(e) =>
            setResultFilters((prev) => ({
              ...prev,
              semesterNumber: e.target.value ? parseInt(e.target.value, 10) : undefined,
            }))
          }
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Tous</option>
          {filters?.semesters.map((s) => (
            <option key={s.number} value={s.number}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Filière</label>
        <select
          value={parentFiliere?.id ?? ''}
          onChange={(e) => setResultFilters((prev) => ({ ...prev, filiereId: e.target.value || undefined }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 max-w-[280px]"
        >
          <option value="">Toutes</option>
          {parentFilieres.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      {filiereOptions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Option</label>
          <select
            value={selectedFiliere?.parentCode ? selectedFiliere.id : ''}
            onChange={(e) =>
              setResultFilters((prev) => ({
                ...prev,
                filiereId: e.target.value || parentFiliere?.id,
              }))
            }
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 max-w-[280px]"
          >
            <option value="">Toutes les options</option>
            {filiereOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.optionName ?? o.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if user has permission to view this page
  if (!canView) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Accès non autorisé</h2>
        <p className="text-gray-500">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <ClipboardList className="w-7 h-7 text-blue-600" />
            Évaluation des Cours
          </h1>
          <p className="text-gray-600 mt-1">Gérez les sondages d'évaluation des cours et consultez les résultats</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Configuration Tab - only visible if canManage */}
          {activeTab === 'config' && canManage && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Sections et Questions du Sondage</h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleResetDefaults}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RotateCcw size={18} />
                    Réinitialiser
                  </button>
                  <button
                    onClick={() => {
                      setEditingSection(null);
                      setShowSectionModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus size={18} />
                    Nouvelle Section
                  </button>
                </div>
              </div>

              {/* Sections List */}
              <div className="space-y-4">
                {sections.map((section) => (
                  <div
                    key={section.id}
                    className={`border rounded-lg ${section.isActive ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                  >
                    {/* Section Header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-grab" />
                        {expandedSections.has(section.id) ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                        <div>
                          <h3 className="font-medium text-gray-900">{section.title}</h3>
                          <p className="text-sm text-gray-500">{section.description || 'Aucune description'}</p>
                        </div>
                        {section.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Par défaut</span>
                        )}
                        {!section.isActive && (
                          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">Inactif</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{section.questions.length} questions</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSection(section);
                            setShowSectionModal(true);
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg"
                        >
                          <Edit2 size={16} className="text-gray-500" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(section.id);
                          }}
                          className="p-2 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </div>

                    {/* Questions List */}
                    {expandedSections.has(section.id) && (
                      <div className="border-t border-gray-100 bg-gray-50 p-4">
                        <div className="space-y-2">
                          {section.questions.map((question, idx) => (
                            <div
                              key={question.id}
                              className={`flex items-center justify-between p-3 bg-white rounded-lg border ${question.isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-400 w-6">{idx + 1}.</span>
                                <div>
                                  <p className="text-sm text-gray-700">{question.questionText}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                      {question.questionType === 'LIKERT_5' ? '1-5' : 'Texte libre'}
                                    </span>
                                    {question.isRequired && (
                                      <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded">Requis</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    setEditingQuestion({ sectionId: section.id, question });
                                    setShowQuestionModal(true);
                                  }}
                                  className="p-2 hover:bg-gray-100 rounded-lg"
                                >
                                  <Edit2 size={14} className="text-gray-500" />
                                </button>
                                <button
                                  onClick={() => handleDeleteQuestion(question.id)}
                                  className="p-2 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => {
                            setEditingQuestion({ sectionId: section.id });
                            setShowQuestionModal(true);
                          }}
                          className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Plus size={16} />
                          Ajouter une question
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Tab - only visible if canManage */}
          {activeTab === 'settings' && canManage && settings && (
            <div className="max-w-2xl space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Paramètres d'Activation</h2>

              {/* Activation Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="font-medium text-gray-900">Activer les sondages</h3>
                  <p className="text-sm text-gray-500">Les étudiants devront compléter le sondage pour voir leurs notes</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settingsForm.isActive ?? true}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Activation Mode — weight-threshold only */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">Mode d'activation : seuil de poids des notes</h3>
                <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-blue-900">Comment ça marche ?</p>
                  <ul className="text-sm text-blue-800 space-y-2 list-disc pl-5">
                    <li>
                      Chaque note d'une matière a un <strong>poids</strong> (ex&nbsp;: Contrôle continu 40%,
                      Examen 60%).
                    </li>
                    <li>
                      Dès que le poids total des notes saisies pour une matière atteint le{' '}
                      <strong>seuil défini ci-dessous</strong>, l'étudiant doit d'abord répondre au sondage
                      d'évaluation de cette matière.
                    </li>
                    <li>
                      Tant que le sondage n'est pas complété, les notes de cette matière restent{' '}
                      <strong>masquées pour l'étudiant et pour ses parents</strong>.
                    </li>
                    <li>Une fois le sondage complété, les notes s'affichent immédiatement.</li>
                  </ul>
                  <p className="text-sm text-blue-700 bg-blue-100 rounded-md p-3">
                    <strong>Exemple</strong> avec un seuil de 50%&nbsp;: si le contrôle continu (40%) et le TP
                    (20%) sont saisis, le poids atteint 60% — le sondage devient obligatoire avant de pouvoir
                    consulter les notes de cette matière.
                  </p>
                </div>
              </div>

              {/* Threshold Percentage */}
              <div className="space-y-2">
                <label className="block font-medium text-gray-900">Seuil de poids cumulatif (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={settingsForm.thresholdPercentage ?? 50}
                  onChange={(e) =>
                    setSettingsForm((prev) => ({ ...prev, thresholdPercentage: parseInt(e.target.value) }))
                  }
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500">
                  Le sondage sera requis quand {settingsForm.thresholdPercentage ?? 50}% du poids des notes
                  d'une matière est saisi
                </p>
              </div>

              <button
                onClick={handleSaveSettings}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enregistrer les paramètres
              </button>
            </div>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-end justify-between">
                {renderFilterSelects()}

                {canExport && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportExcel}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      <FileSpreadsheet size={18} className="text-green-600" />
                      Excel
                    </button>
                  </div>
                )}
              </div>

              {/* Summary Cards */}
              {summary && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Réponses</p>
                    <p className="text-2xl font-bold text-blue-700">{summary.totalResponses}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Taux de réponse</p>
                    <p className="text-2xl font-bold text-green-700">{summary.responseRate.toFixed(1)}%</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <p className="text-sm text-purple-600 font-medium">Moyenne générale</p>
                    <p className="text-2xl font-bold text-purple-700">{summary.overallAverage.toFixed(2)}/5</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4">
                    <p className="text-sm text-amber-600 font-medium">Étudiants</p>
                    <p className="text-2xl font-bold text-amber-700">{summary.totalStudents}</p>
                  </div>
                </div>
              )}

              {/* View Selector */}
              <div className="flex gap-2">
                {[
                  { key: 'summary', label: 'Résumé' },
                  { key: 'professor', label: 'Par Professeur' },
                  { key: 'matiere', label: 'Par Matière' },
                  { key: 'comments', label: 'Commentaires' },
                ].map((v) => (
                  <button
                    key={v.key}
                    onClick={() => setResultsView(v.key as any)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      resultsView === v.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>

              {/* Results Content */}
              {resultsView === 'summary' && summary && (
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900">Moyennes par Section</h3>
                  <div className="space-y-3">
                    {summary.sectionAverages.map((section) => (
                      <div key={section.sectionId} className="flex items-center gap-4">
                        <div className="w-48 text-sm text-gray-700">{section.sectionTitle}</div>
                        <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(section.average / 5) * 100}%` }}
                          />
                        </div>
                        <div className="w-16 text-right font-medium text-gray-900">{section.average.toFixed(2)}/5</div>
                        <div className="w-24 text-right text-sm text-gray-500">{section.responseCount} rép.</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resultsView === 'professor' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                        <th className="px-4 py-3 text-left text-sm font-medium">Professeur</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Cours</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Moyenne</th>
                      </tr>
                    </thead>
                    <tbody>
                      {professorResults.map((prof) => (
                        <tr key={prof.professorId} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{prof.professorName}</td>
                          <td className="px-4 py-3 text-center text-gray-600">{prof.coursesCount}</td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`font-bold ${
                                prof.averageRating >= 4
                                  ? 'text-green-600'
                                  : prof.averageRating >= 3
                                    ? 'text-blue-600'
                                    : 'text-amber-600'
                              }`}
                            >
                              {prof.averageRating.toFixed(2)}/5
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {resultsView === 'matiere' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                        <th className="px-4 py-3 text-left text-sm font-medium">Matière</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Groupe</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Professeur</th>
                        <th className="px-4 py-3 text-center text-sm font-medium">Moyenne</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matiereResults.map((m, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{m.moduleElementName}</td>
                          <td className="px-4 py-3 text-gray-600">{m.groupeName}</td>
                          <td className="px-4 py-3 text-gray-600">{m.professorName}</td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`font-bold ${
                                m.averageRating >= 4
                                  ? 'text-green-600'
                                  : m.averageRating >= 3
                                    ? 'text-blue-600'
                                    : 'text-amber-600'
                              }`}
                            >
                              {m.averageRating.toFixed(2)}/5
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {resultsView === 'comments' && (
                <div className="space-y-4">
                  {comments.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">Aucun commentaire pour les filtres sélectionnés</p>
                  ) : (
                    comments.map((c, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 mb-2 text-sm text-gray-500">
                          <MessageSquare size={14} />
                          <span>{c.moduleElementName}</span>
                          <span>•</span>
                          <span>{c.professorName}</span>
                          <span>•</span>
                          <span>{c.groupeName}</span>
                        </div>
                        <p className="text-gray-700 italic">"{c.textResponse}"</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Participation Tab */}
          {activeTab === 'participation' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Taux de Participation</h2>
              </div>

              {renderFilterSelects()}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                      <th className="px-4 py-3 text-left text-sm font-medium">Matière</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Groupe</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Professeur</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Total</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Complétés</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">En attente</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participation.map((p, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{p.moduleElementName}</td>
                        <td className="px-4 py-3 text-gray-600">{p.groupeName}</td>
                        <td className="px-4 py-3 text-gray-600">{p.professorName}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{p.totalStudents}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="flex items-center justify-center gap-1 text-green-600">
                            <CheckCircle size={14} />
                            {p.completedCount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="flex items-center justify-center gap-1 text-amber-600">
                            <AlertCircle size={14} />
                            {p.pendingCount}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`font-bold ${
                              p.completionRate >= 75
                                ? 'text-green-600'
                                : p.completionRate >= 50
                                  ? 'text-blue-600'
                                  : 'text-red-600'
                            }`}
                          >
                            {p.completionRate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section Modal */}
      {showSectionModal && (
        <SectionModal
          section={editingSection}
          onClose={() => {
            setShowSectionModal(false);
            setEditingSection(null);
          }}
          onSave={async (data) => {
            try {
              if (editingSection) {
                await updateSurveySection(editingSection.id, data);
              } else {
                await createSurveySection(data as any);
              }
              const sectionsData = await getSurveySections();
              setSections(sectionsData);
              setShowSectionModal(false);
              setEditingSection(null);
            } catch (error) {
              console.error('Error saving section:', error);
            }
          }}
        />
      )}

      {/* Question Modal */}
      {showQuestionModal && editingQuestion && (
        <QuestionModal
          sectionId={editingQuestion.sectionId}
          question={editingQuestion.question}
          onClose={() => {
            setShowQuestionModal(false);
            setEditingQuestion(null);
          }}
          onSave={async (data) => {
            try {
              if (editingQuestion.question) {
                await updateSurveyQuestion(editingQuestion.question.id, data);
              } else {
                await createSurveyQuestion({ sectionId: editingQuestion.sectionId, ...data } as any);
              }
              const sectionsData = await getSurveySections();
              setSections(sectionsData);
              setShowQuestionModal(false);
              setEditingQuestion(null);
            } catch (error) {
              console.error('Error saving question:', error);
            }
          }}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText="Confirmer"
        cancelText="Annuler"
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}

// Section Modal Component
function SectionModal({
  section,
  onClose,
  onSave,
}: {
  section: SurveySection | null;
  onClose: () => void;
  onSave: (data: Partial<SurveySection>) => void;
}) {
  const [title, setTitle] = useState(section?.title ?? '');
  const [description, setDescription] = useState(section?.description ?? '');

  // Auto-generate code from title
  const generateCode = (text: string) => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '') // Remove special chars
      .trim()
      .replace(/\s+/g, '_'); // Replace spaces with underscores
  };

  const handleSave = () => {
    const code = section?.code || generateCode(title) || `SECTION_${Date.now()}`;
    onSave({ code, title, description });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{section ? 'Modifier la Section' : 'Nouvelle Section'}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Contenu du Cours"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optionnel)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description de la section..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!title}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

// Question Modal Component
function QuestionModal({
  sectionId: _sectionId,
  question,
  onClose,
  onSave,
}: {
  sectionId: string;
  question?: any;
  onClose: () => void;
  onSave: (data: any) => void;
}) {
  const [questionText, setQuestionText] = useState(question?.questionText ?? '');
  const [questionType, setQuestionType] = useState(question?.questionType ?? 'LIKERT_5');
  const [isRequired, setIsRequired] = useState(question?.isRequired ?? true);

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{question ? 'Modifier la Question' : 'Nouvelle Question'}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="L'enseignant explique clairement les concepts..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="LIKERT_5">Échelle 1-5 (Likert)</option>
              <option value="FREE_TEXT">Texte libre</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isRequired"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="isRequired" className="text-sm text-gray-700">
              Réponse obligatoire
            </label>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Annuler
          </button>
          <button
            onClick={() => onSave({ questionText, questionType, isRequired })}
            disabled={!questionText}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
